import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@corpuscomplete/web/components/link";
import { useStore } from "@tanstack/react-form";
import { RecipeForm } from "@corpuscomplete/core-ui/nutrition/recipe/form";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { RecipeFormComponent } from "../components/recipe-form";
import { RecipeAuthoringPageShell } from "../components/recipe-authoring-page-shell";
import { Scope } from "@corpuscomplete/core/common/scope";
import { ScopeBreadcrumb } from "@corpuscomplete/web/modules/common/components/scope-breadcrumb";

export function RecipeCreatePage({
  initialCollectionId,
  scope,
}: {
  initialCollectionId?: string;
  scope: Scope.Info;
}) {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  return (
    <RecipeForm.CreateProvider
      collectionScopeId={Scope.id(scope)}
      initialCollectionId={initialCollectionId}
      onCreated={({ recipeId }) => {
        setSubmitError(null);
        void navigate({
          to: "/recipes/$id",
          params: { id: recipeId },
        });
      }}
      onCreateError={(error) => {
        setSubmitError(error.message);
      }}
    >
      <RecipeCreatePageContent
        scope={scope}
        submitError={submitError}
        onClearError={() => setSubmitError(null)}
      />
    </RecipeForm.CreateProvider>
  );
}

function RecipeCreatePageContent({
  scope,
  submitError,
  onClearError,
}: {
  scope: Scope.Info;
  submitError: string | null;
  onClearError: () => void;
}) {
  const navigate = useNavigate();
  const { form, isSyncing, clear } = RecipeForm.useCreate();
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const listRoute =
    scope.kind === "personal"
      ? ({ to: "/personal/recipes" } as const)
      : ({ to: "/spaces/$spaceId/recipes", params: { spaceId: scope.spaceId } } as const);

  return (
    <RecipeAuthoringPageShell
      breadcrumb={
        <ScopeBreadcrumb
          scope={scope}
          scopeBehavior="static"
          crumbs={[
            {
              label: "Recipes",
              renderLink:
                scope.kind === "personal" ? (
                  <Link to="/personal/recipes" />
                ) : (
                  <Link to="/spaces/$spaceId/recipes" params={{ spaceId: scope.spaceId }} />
                ),
            },
            { label: "Create", current: true },
          ]}
        />
      }
      isSubmitting={isSubmitting}
      submitError={submitError}
      headerStatus={
        isSyncing ? (
          <Text variant="sm" className="text-muted-foreground">
            Saving changes...
          </Text>
        ) : undefined
      }
      headerActions={
        <Button
          variant="destructive"
          size="sm"
          disabled={isSubmitting}
          onClick={() => {
            onClearError();
            clear();
            void navigate(listRoute);
          }}
        >
          Discard Draft
        </Button>
      }
      onCancel={() => {
        onClearError();
        void navigate(listRoute);
      }}
      onSave={() => {
        onClearError();
        void form.handleSubmit();
      }}
    >
      <RecipeFormComponent form={form} />
    </RecipeAuthoringPageShell>
  );
}
